/**
 * Date tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_meaning`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dateTattoosPack: ToilePack = {
    slug: 'date-tattoos',
    category: 'tattoo',
    name: 'Date tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'roman', label: 'Roman numerals', subject: 'the date written in Roman numerals as "XII · IV · MMXXI" in one straight line, small dots between the parts'},
        {key: 'heartbeat', label: 'Date on heartbeat', subject: 'the date "12.04.21" written in small numbers set into a heartbeat line'},
        {key: 'clock', label: 'Clock and date', subject: 'a small pocket-watch face showing the time of a birth, with the date "12.04.21" written beneath it'},
    ],
    styles: [
        {key: 'engraved', label: 'Engraved serif', style: 'an engraved serif face, sharp bracketed serifs, high contrast between thick stems and hairlines', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
        {key: 'typewriter', label: 'Typewriter', style: 'a typewriter face, even monoline strokes with slab serifs and slightly uneven ink', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
        {key: 'handdrawn', label: 'Hand drawn', style: 'a small hand-drawn hand, light monoline strokes with slightly uneven spacing, like a pen on a notebook page', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
    ],
    directions: ['red ink', 'wrist', 'two dates'],
    directionHint: 'try: red ink',
    seo: {
        title: 'Date tattoo designs: Roman numerals, heartbeat, clock',
        metaDescription: 'Nine date tattoo designs: 12 April 2021 as Roman numerals, on a heartbeat line and in a pocket watch, in three lettering styles.',
        h1: 'Date tattoo designs: Roman numerals, heartbeat, clock face',
        tagline: 'The sample date 12 April 2021 in three layouts and three lettering styles.',
        intro: 'A birth, a wedding or a loss is often marked with a date, in Roman numerals along a forearm or collarbone. The sheet shows the sample date 12 April 2021 as Roman numerals in one line, as plain numbers set into a heartbeat line, and inside a small pocket-watch face, each in engraved serif, typewriter and hand drawn lettering. Type your own date, and the same three layouts carry it.',
        useCases: [
            'a birth date set in a short line of numerals',
            'a wedding date on a forearm or collarbone',
            'a date to show the artist before booking',
        ],
        faq: [
            {q: 'What is the sample date?', a: 'The sample date is 12 April 2021. Type your own date to see it in the same three layouts.'},
            {q: 'How do the Roman numerals read?', a: 'The sample reads XII, IV and MMXXI: twelve, four and 2021, with dots between them.'},
            {q: 'What is the difference between the lettering styles?', a: 'Engraved serif uses sharp serifs with high contrast, typewriter uses even strokes with slab serifs, and hand drawn uses light strokes with slightly uneven spacing.'},
            {q: 'Can I type my own date?', a: 'Yes. Type the date and the three layouts are applied to it. The result is a request, not a promised image.'},
        ],
    },
}
